import React, { FC, memo } from "react";
import { Link } from "react-router-dom";
import NavHoverLocator from "../molecules/NavHoverLocator";
import { accessCardLink } from "../../services/ssoService";
import { useTrackPageEffect } from "hooks/services/useTrackPageEffect";
import {
  Box,
  Stack,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { AuditEvent } from "types/track/AuditEventType";
import FAQAccordion from "components/molecules/FAQAccordion/FAQAccordion";

const FAQs: FC = memo(() => {
  useTrackPageEffect({
    eventType: AuditEvent.MemberPortalViewFaq,
  });

  return (
    <Stack pt={1} pb={2} spacing={1}>
      <Typography variant="h2" fontWeight="bold" component="h1" mb={4}>
        Frequently Asked Questions
      </Typography>
      <FAQAccordion title="What can I do through my online Alignment Health Plan member account?">
        <Box>
          <Typography variant="body1" component="p">
            On members.alignmenthealthplan.com, you can find information related
            to your plan benefits and coverage 24 hours a day, seven days a
            week. Once logged into our secure site, you can:
          </Typography>
          <Box component="ul" ml={2}>
            <Box component="li">
              <Typography>
                Access general member forms and information under the{" "}
                <Typography fontWeight="bold" component="span">
                  <NavHoverLocator>Forms &amp; Resources</NavHoverLocator>
                </Typography>{" "}
                menu{" "}
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Find your plan's specific benefit information under{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/benefits">Benefit Highlights</Link>
                </Typography>
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Access a doctor 24/7 by phone or video and find an in-network
                doctor or hospital under the{" "}
                <Typography fontWeight="bold" component="span">
                  <NavHoverLocator>Doctors &amp; Care</NavHoverLocator>
                </Typography>{" "}
              </Typography>
              menu
            </Box>
            <Box component="li">
              <Typography>
                Find an in-network pharmacy and view your prescription history
                under the{" "}
                <Typography fontWeight="bold" component="span">
                  <NavHoverLocator>Prescriptions</NavHoverLocator>
                </Typography>{" "}
                menu
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Search through recent{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/claims">medical claims</Link>
                </Typography>{" "}
                under the Payments menu
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Review the{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/explanation-of-benefits">
                    Explanation of Benefits
                  </Link>
                </Typography>
                under the{" "}
                <Typography fontWeight="bold" component="span">
                  <NavHoverLocator>Forms &amp; Resources</NavHoverLocator>
                </Typography>{" "}
                menu
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                View and download your member ID card under{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/id-card">My ID Card</Link>
                </Typography>
              </Typography>
            </Box>
            <Box component="li">
              Send your Concierge team a message on{" "}
              <Typography>
                View plan documents like your Evidence of Coverage booklet under{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/plan-documents">Plan Documents</Link>
                </Typography>
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Send your Concierge team a message on{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/message-center">My Message Center</Link>
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Box>
      </FAQAccordion>
      <FAQAccordion
        title=" How do I send a message through My Message Center? How will I know
          that someone has responded to me?"
      >
        <Box>
          <Typography variant="body1" component="p" mb={2}>
            As a member, you can send us a secure message 24 hours a day, seven
            days a week through{" "}
            <Typography fontWeight="bold" component="span">
              <Link to="/message-center">My Message Center</Link>
            </Typography>
            . Just follow the steps below:
          </Typography>
          <Box component="ul" ml={2}>
            <Box component="li">
              <Typography>
                Click on{" "}
                <Typography fontWeight="bold" component="span">
                  <Link to="/message-center">My Message Center</Link>
                </Typography>
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Send a new message by clicking{" "}
                <Typography fontWeight="bold" component="span">
                  Write a New Message
                </Typography>
              </Typography>
            </Box>
            <Box component="li">
              <Typography>
                Make sure to include a category, subject, and message before
                clicking{" "}
                <Typography fontWeight="bold" component="span">
                  Send
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Typography component="p">
            {`My Message Center keeps messages you previously sent to us and any replies you've
                received from your ACCESS On-Demand Concierge team. Once you receive a message
                response, you will be notified via email. If you have any unread messages in your
                inbox, you will see a red notification on the envelope icon on the top of your
                screen.`}
          </Typography>
        </Box>
      </FAQAccordion>

      <FAQAccordion
        title="How can I change my contact information on file such as my home
          address, phone number, or email address?"
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            component="h2"
            mb={4}
          ></Typography>
          <Typography>
            If you need to make any changes to your contact information,{" "}
            <Link to="/contact-us">call</Link> your 24/7 ACCESS On-Demand
            Concierge or send us a message through My Message Center.
          </Typography>
        </Box>
      </FAQAccordion>
      <FAQAccordion title=" What is the ACCESS On-Demand Concierge program?">
        <Typography mb={2}>
          {`Your health care needs can happen any time - that's why we launched our ACCESS
                On-Demand Concierge program, exclusively for Alignment Health Plan members. ACCESS
                On-Demand Concierge gives you access 24 hours a day, seven days a week to:`}
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Box component="li">A licensed doctor by phone or video</Box>
          <Box component="li">
            A{" "}
            <Typography component="span" fontWeight="bold">
              concierge team
            </Typography>{" "}
            dedicated to helping you navigate the services and benefits
            available to you with speed, ease and efficiency - from making
            appointments, scheduling transportation, accessing benefits, or
            discussing health-related topics with a clinician
          </Box>
          <Box component="li">
            Information about your benefits via{" "}
            <Typography component="span" fontWeight="bold">
              phone
            </Typography>
            ,{" "}
            <Typography component="span" fontWeight="bold">
              web
            </Typography>{" "}
            and{" "}
            <Typography component="span" fontWeight="bold">
              mobile app
            </Typography>
          </Box>
          <Box component="li">
            {`Your ACCESS On-Demand Concierge "black card" to purchase eligible items at
                    participating retailers (Benefits vary by plan. Copays and certain restrictions
                    may apply.)`}
          </Box>
        </Box>
      </FAQAccordion>
      <FAQAccordion title="How can I earn rewards through ACCESS On-Demand Concierge?">
        <Typography>
          Members of eligible plans can receive rewards for completing select
          healthy activities and preventive screenings. Upon completion of the
          activity, reward dollars will be loaded onto your ACCESS card and can
          be used to purchase eligible items at participating retailers. For
          more information, <Link to="/contact-us">call</Link> your 24/7 ACCESS
          On-Demand Concierge or send us a message through{" "}
          <Typography fontWeight="bold" component="span">
            <Link to="/message-center">My Message Center</Link>
          </Typography>
          .
        </Typography>
      </FAQAccordion>
      <FAQAccordion title="Where can I check my ACCESS card balance?">
        <Typography variant="body1" component="p">
          You can check your{" "}
          <Typography fontWeight="bold" component="span">
            <MuiLink
              tabIndex={0}
              className="ptr"
              href={accessCardLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              ACCESS Card Balance
            </MuiLink>
          </Typography>{" "}
          online under the{" "}
          <Typography fontWeight="bold" component="span">
            <NavHoverLocator>Plan &amp; Benefits</NavHoverLocator>
          </Typography>{" "}
          menu. You can also{" "}
          <Typography fontWeight="bold" component="span">
            <Link to="/contact-us">call</Link>
          </Typography>{" "}
          your ACCESS On-Demand Concierge or send us a message through{" "}
          <Typography fontWeight="bold" component="span">
            <Link to="/message-center">My Message Center</Link>
          </Typography>
          .
        </Typography>
      </FAQAccordion>
      <FAQAccordion title="What is the difference between the Alignment Health Plan member ID card and the black ACCESS On-Demand Concierge card?">
        <Typography component="p">
          Your Alignment Health Plan member ID card is regulated by the Centers
          for Medicare &amp; Medicaid Services (CMS) and includes your ID
          number, plan type, prescription (Rx) benefits, and member services
          phone line.{" "}
          <Typography fontWeight="bold" component="span" fontStyle="italic">
            You will need to show this card to access covered medical and
            prescription drug benefits
          </Typography>
          {`at the doctor's office or at the pharmacy for prescription refills.`}
        </Typography>
        <Typography component="p">
          Your ACCESS On-Demand Concierge card connects members to ACCESS
          On-Demand Concierge features. It is black with the Alignment
          Healthcare logo, your name and unique ACCESS account number.
        </Typography>
        <Typography variant="body1" component="p">
          If you have lost your member ID card or need a replacement,{" "}
          <Typography fontWeight="bold" component="span">
            <Link to="/contact-us">call</Link>
          </Typography>{" "}
          your 24/7 ACCESS On-Demand Concierge, or send us a message through{" "}
          <Typography fontWeight="bold" component="span">
            <Link to="/message-center">My Message Center</Link>
          </Typography>
          .
        </Typography>
      </FAQAccordion>
      <FAQAccordion title="What is the ACCESS On-Demand Concierge phone number?">
        <Typography variant="body1" component="p">
          <Typography fontWeight="bold" component="span">
            1-833-AHC-ACCESS (1-833-242-2223), TTY 711
          </Typography>
        </Typography>

        <Typography variant="body1" component="p">
          Available 24 hours a day, seven days a week, 365 days a year
        </Typography>
      </FAQAccordion>
      <FAQAccordion title="  What is the Alignment Health Plan Member Services phone number?">
        <Box>
          <Typography variant="body1" component="p">
            <Typography fontWeight="bold" component="span">
              1-866-634-2247, TTY 711
            </Typography>
          </Typography>

          <Typography variant="body1" component="p">
            Member Services representatives are available 8 a.m. to 8 p.m. seven
            days a week Oct. 1 - March 31 (except Thanksgiving and Christmas),
            and 8 a.m. to 8 p.m. Monday-Friday April 1 - Sept. 30 (excluding
            holidays).
          </Typography>
        </Box>
      </FAQAccordion>
    </Stack>
  );
});
FAQs.displayName = "FAQs";

export default FAQs;
